/**
 * Application configuration settings
 */

// Import API base URL from the centralized config
import { API_BASE_URL, API_FEATURES } from "./api/config";

// API configuration
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  ENDPOINTS: {
    // Listings
    LISTINGS: "/api/listings",
    LISTING_DETAIL: (id: string) => `/api/listings/${id}`,
    BOOST_LISTING: (id: string) => `/api/listings/${id}/boost`,
    FEATURE_LISTING: (id: string) => `/api/listings/${id}/feature`,
    USER_LISTINGS: (userId: string) => `/api/listings?userId=${userId}`,

    // Authentication
    SIGNUP: "/api/auth/register",
    LOGIN: "/api/auth/login",
    USER_PROFILE: (userId: string) => `/api/users/${userId}`,

    // Reference Data
    PROPERTY_TYPES: "/api/property-types",
    PROVINCES: "/api/locations/provinces",
    DISTRICTS: "/api/locations/districts",
    CITIES: "/api/locations/cities",
    AMENITIES: "/api/amenities",

    // Points & Transactions
    USER_POINTS: "/api/users/points",
    PURCHASE_POINTS: "/api/users/purchase-points",
    TRANSACTIONS: "/api/transactions",
    USER_TRANSACTIONS: (userId: string) => `/api/transactions/user/${userId}`,
    TOP_UP_POINTS: "/api/users/purchase-points",
    ADJUST_POINTS: (userId: string) => `/api/admin/users/${userId}/points`,

    // Settings & Admin
    ROLE_LIMITS: "/api/admin/limits",
    POINTS_CONFIG: "/api/admin/points",

    // Saved Items
    SAVED_LISTINGS: "/api/saved-listings",
    REMOVE_SAVED_LISTING: (id: string) => `/api/saved-listings/${id}`,
  },
};

// Analytics configuration
export const ANALYTICS_CONFIG = {
  // Google Analytics Measurement ID (GA4)
  MEASUREMENT_ID: import.meta.env.VITE_GA_MEASUREMENT_ID || "G-XXXXXXXXXX",
  // Enable analytics in production by default, or if explicitly enabled in env
  ENABLED:
    import.meta.env.PROD || import.meta.env.VITE_ENABLE_ANALYTICS === "true",
  // Enable debug logs in development or if explicitly enabled in env
  DEBUG_LOGS: API_FEATURES.DEBUG_LOGS,
};

// Feature flags
export const FEATURES = {
  // Always use real API - reuse the value from API_FEATURES
  USE_REAL_API: API_FEATURES.USE_REAL_API,
  // Enable debug logs in development or if explicitly enabled in env
  DEBUG_LOGS: API_FEATURES.DEBUG_LOGS,
};

// Default pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: parseInt(import.meta.env.VITE_DEFAULT_PAGE_SIZE || "10", 10),
};

// Type for query parameters to prevent 'any' type
export type QueryParams = Record<
  string,
  string | number | boolean | undefined | null
>;

// Function to build a full API URL
export const buildApiUrl = (
  endpoint: string,
  queryParams?: QueryParams
): string => {
  // Always use the central API_BASE_URL
  const baseUrl = API_BASE_URL;

  const url = `${baseUrl}${endpoint}`;

  if (!queryParams) {
    return url;
  }

  const params = new URLSearchParams();
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
};
